import { useEffect, useCallback, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/state';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useUpdateWorkflowStageMutation } from '@/__generated__/graphql';
import { useFeatureFlag } from '@/hooks';
import { useSnackBarMessage, sortWorkflowStages } from '@/components/ZonesManagementRevamped/utils';
import { Title, tooltipStyles } from './styles';
import { abyssColors, primary } from '@/theme/colors';
import { FINAL_WORKFLOW_STAGE } from '@/constants';
import { titleCase } from '@/utils';

type EditableStageNameProps = {
  workflowStageId: string;
  workflowStageName: string;
};

export const EditableStageName = ({
  workflowStageId,
  workflowStageName,
}: EditableStageNameProps) => {
  const structureId = useAtomValue(state.selectedStructureId);
  const setWorkflowStages = useSetAtom(state.workflowStages);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const canEditStageName =
    useFeatureFlag(['admin', 'ait-task-lead']) &&
    titleCase(workflowStageName) !== FINAL_WORKFLOW_STAGE;

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  useEffect(() => {
    setName(workflowStageName);
  }, [workflowStageName]);

  const [updateStageName, { loading: updating, error }] = useUpdateWorkflowStageMutation();

  const saveName = useCallback(() => {
    if (name.trim() && name !== workflowStageName && structureId) {
      updateStageName({
        variables: {
          input: {
            structureId: structureId,
            workflowStageId: workflowStageId,
            workflowStageName: name.trim(),
          },
        },
        onCompleted: (response) => {
          if (response?.updateWorkflowStage?.workflowStages) {
            const sortedStages = sortWorkflowStages(
              response?.updateWorkflowStage?.workflowStages?.filter(
                (stage): stage is NonNullable<typeof stage> => stage !== null
              )
            );
            setWorkflowStages(sortedStages);
            showSnackBar('The stage name has been updated successfully', 'success');
            setIsEditing(false);
          }
        },
        onError: (errorResponse) => {
          console.error(errorResponse);
          showSnackBar(`${errorResponse.message}`, 'error');
          setIsEditing(false);
        },
      });
    }
    setIsEditing(false);
  }, [
    workflowStageId,
    workflowStageName,
    name,
    setWorkflowStages,
    showSnackBar,
    structureId,
    updateStageName,
  ]);

  return (
    <>
      {isEditing && canEditStageName ? (
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveName();
            else if (e.key === 'Escape') {
              setName(workflowStageName);
              setIsEditing(false);
            }
          }}
          size="small"
          autoFocus
          disabled={updating}
          helperText={error ? 'Failed to save' : undefined}
          sx={{
            '& .MuiInputBase-root': {
              height: '3.2rem',
              minWidth: '12rem',
              padding: '0 0.8rem',
              color: primary.independence,
              fontSize: '1.6rem',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '100%',
              borderRadius: '0.2rem',
              border: `0.1rem solid ${abyssColors.primary[300]}`,
              '& fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              padding: 0,
            },
            '& .MuiOutlinedInput-root': {
              width: `${Math.max(name.length * 1.2, 12)}ch`,
            },
          }}
        />
      ) : (
        <Tooltip
          title="Click to edit stage name"
          arrow
          disableHoverListener={!canEditStageName}
          sx={{ tooltipStyles }}
        >
          <Title
            onClick={() => {
              if (canEditStageName) setIsEditing(true);
            }}
            sx={{
              cursor: canEditStageName ? 'pointer' : 'default',
              userSelect: canEditStageName ? 'auto' : 'none',
            }}
          >
            {workflowStageName}
          </Title>
        </Tooltip>
      )}
    </>
  );
};
